// The numbers beside the console's nav (APS-194, design "Overview"; Console D, APS-308): the alert
// rules firing, and the issues new in the release (the bar's, else the app's newest), else the
// unresolved ones. Each is refreshed every minute. The sandbox has no alert rules (D64), so no
// alerts number there; its issues count still shows.
import { useQuery } from '@tanstack/react-query'
import { alertRulesQuery, issueCountsQuery, windowReleasesQuery } from '../api/queries'
import { sandboxFor } from '../sandbox'
import { firingRules } from './overview'
import { busiestApp, releasesNewestFirst } from './releases'

export interface NavCounts {
  /** Undefined while unknown: the nav shows no number rather than a wrong one. */
  alerts: number | undefined
  issues: number | undefined
  /** The issues new in `release`, as the list's New tab counts them. */
  fresh?: { count: number; release: string } | undefined
}

/** The issues list's default range. */
const ISSUE_DAYS = 7

export function useNavCounts(
  orgId: string,
  projectId: string,
  scope: { app?: string | undefined; release?: string | undefined } = {},
): NavCounts {
  const sandbox = sandboxFor(projectId)
  const rules = useQuery({
    ...alertRulesQuery(orgId, projectId),
    enabled: !sandbox,
    refetchInterval: 60_000,
  })
  // The same query as the list's Unresolved tab with no filter, so the two share one answer.
  // No retry: a server without the route answers 404, and the badge shows nothing.
  const counts = useQuery({
    ...issueCountsQuery(projectId, { days: ISSUE_DAYS }),
    enabled: !!projectId,
    retry: false,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  })
  // The release "new" is measured in, found as the list finds it (the same releases query).
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const releases = useQuery({
    ...windowReleasesQuery(projectId, 30, now),
    enabled: !!projectId && !scope.release,
    retry: false,
  })
  const release =
    scope.release ??
    (releases.data
      ? releasesNewestFirst(releases.data, scope.app ?? busiestApp(releases.data))[0]?.release
      : undefined)
  const inRelease = useQuery({
    ...issueCountsQuery(projectId, { app_id: scope.app, release, days: ISSUE_DAYS }),
    enabled: !!projectId && !!release,
    retry: false,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  })
  const fresh = inRelease.data?.new
  return {
    alerts: rules.data ? firingRules(rules.data.rules).length : undefined,
    issues: counts.data?.unresolved,
    fresh: release && fresh !== undefined ? { count: fresh, release } : undefined,
  }
}
